import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, Landmark, QrCode, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ErrorState, LoadingState, Notice, PageHeader, Spinner, StatusBadge } from "@/components/common/States";
import { api, errorMessage, fetcher } from "@/lib/api";
import { rupiah, SETTLEMENT_STATUS } from "@/lib/labels";

const uang = (v) => (v === undefined || v === null ? "—" : rupiah(v));

// Satu layar satu pekerjaan: reseller mengonfirmasi uang masuk dari sekolah binaan,
// lalu menyetor kulakan Rp2.000/akun ke founder memakai QRIS statis di layar ini.
// Setoran kini per sekolah — setiap sekolah punya rincian akun dan nominal kulakan sendiri.
export default function MitraPembayaran() {
  const qc = useQueryClient();
  const [periode, setPeriode] = useState("");
  const q = useQuery({
    queryKey: ["mitra", "pembayaran", periode],
    queryFn: () => fetcher(`/reseller/pembayaran${periode ? `?periode=${periode}` : ""}`),
  });
  const [schoolId, setSchoolId] = useState("");
  const [jumlah, setJumlah] = useState("");
  const [metode, setMetode] = useState("qris");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  if (q.isLoading) return <LoadingState rows={6} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const g = d.gambaran;
  const f = d.kanal_founder || {};
  const periodeList = [...new Set([d.periode, ...(d.periode_tersedia || [])])].sort().reverse();
  const adaKlaim = d.sekolah.some((s) => s.menunggu_konfirmasi > 0);
  const founderKanal = [f.qris_label && `QRIS ${f.qris_label}`, f.qris_nmid, f.bank_account && `Rekening ${f.bank_account}`]
    .filter(Boolean).join(" · ");
  const perSekolah = g.per_sekolah || [];
  const sekolahTerpilih = perSekolah.find((s) => s.school_id === schoolId);
  const sisaSekolah = sekolahTerpilih ? Math.max(0, sekolahTerpilih.nominal_kulakan - (sekolahTerpilih.sudah_disetor || 0)) : 0;

  const reload = async () => {
    setJumlah("");
    setCatatan("");
    await qc.invalidateQueries({ queryKey: ["mitra", "pembayaran"] });
    await qc.invalidateQueries({ queryKey: ["mitra", "beranda"] });
  };

  const konfirmasi = async (s) => {
    setBusy(s.school_id);
    setErr("");
    try {
      const { data } = await api.post("/reseller/pembayaran/konfirmasi", { school_id: s.school_id });
      toast.success(`${s.school_name}: ${data.dikonfirmasi} akun dikonfirmasi sudah bayar ke Anda.`);
      await reload();
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy("");
    }
  };

  const setor = async () => {
    const n = Number(String(jumlah).replace(/\D/g, ""));
    if (!n || n <= 0) return setErr("Isi jumlah setoran dengan angka rupiah, contoh 40000.");
    if (!schoolId) return setErr("Pilih sekolah dulu sebelum menyetor.");
    setBusy("setor");
    setErr("");
    try {
      await api.post("/reseller/setoran", { periode: d.periode, school_id: schoolId, amount_idr: n, metode, catatan: catatan || undefined });
      toast.success(`Laporan setoran untuk ${sekolahTerpilih.school_name} terkirim. Founder akan memverifikasi.`);
      setSchoolId("");
      await reload();
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Ruang Mitra SIST" title="Kelola Pembayaran"
        description="Konfirmasi pembayaran sekolah binaan, lalu setor kulakan ke founder memakai QRIS di bawah. Harga kulakan dan setoran adalah urusan Anda dengan founder — sekolah tidak melihat bagian ini."
        actions={
          <Select value={d.periode} onValueChange={setPeriode}>
            <SelectTrigger aria-label="Periode" className="w-40" data-testid="mitra-payment-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{periodeList.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        } />

      {err && <Notice tone="danger" testId="mitra-payment-error">{err}</Notice>}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="rounded-2xl border bg-card p-4" data-testid="mitra-payment-akun">
          <p className="font-brand text-2xl font-extrabold tabular-nums">{g.akun}</p>
          <p className="text-xs text-muted-foreground">Akun ditagih</p>
        </div>
        <div className="rounded-2xl border bg-card p-4" data-testid="mitra-payment-dibayar">
          <p className="font-brand text-2xl font-extrabold tabular-nums text-emerald-600">{g.akun_dibayar_ke_reseller}</p>
          <p className="text-xs text-muted-foreground">Dilaporkan bayar oleh sekolah</p>
        </div>
        <div className="rounded-2xl border bg-card p-4" data-testid="mitra-payment-kulakan">
          <p className="font-brand text-2xl font-extrabold tabular-nums">{uang(g.nominal_kulakan)}</p>
          <p className="text-xs text-muted-foreground">Kulakan {uang(g.wholesale_price_idr)}/akun</p>
        </div>
        <div className="rounded-2xl border bg-card p-4" data-testid="mitra-payment-sisa">
          <p className="font-brand text-2xl font-extrabold tabular-nums text-[#E11D2E]">{uang(g.sisa_kulakan)}</p>
          <p className="text-xs text-muted-foreground">Sisa kulakan ke founder</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border bg-card p-5 space-y-4" data-testid="mitra-payment-confirm-section">
          <h2 className="flex items-center gap-2 text-base font-bold">
            <BadgeCheck className="h-4 w-4 text-[#0B3D91]" aria-hidden /> Konfirmasi pembayaran sekolah
          </h2>
          {!adaKlaim && <p className="text-sm text-muted-foreground">Tidak ada laporan bayar yang menunggu konfirmasi pada periode ini.</p>}
          {d.sekolah.map((s) => (
            <div key={s.school_id} className="rounded-xl border p-4 space-y-2" data-testid={`mitra-payment-school-${s.school_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{s.school_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.akun} akun · {uang(s.nominal_jual)} · {s.menunggu_bayar} belum bayar · {s.lunas} lunas
                  </p>
                </div>
                {s.menunggu_konfirmasi > 0 && (
                  <Button size="sm" onClick={() => konfirmasi(s)} disabled={busy === s.school_id}
                    data-testid={`mitra-payment-confirm-${s.school_id}`}>
                    {busy === s.school_id ? <Spinner /> : <BadgeCheck className="h-4 w-4 mr-1.5" />}
                    Konfirmasi {s.menunggu_konfirmasi} akun
                  </Button>
                )}
              </div>
              {s.menunggu_konfirmasi > 0 && (
                <p className="text-xs text-muted-foreground">
                  {s.menunggu_konfirmasi} akun dilaporkan sudah dibayar oleh sekolah{ s.claim_baru > 0 ? " (laporan dari Admin Sekolah)" : "" }. Konfirmasi bila uangnya sudah masuk ke rekening/QRIS Anda.
                </p>
              )}
            </div>
          ))}
          {d.sekolah.length === 0 && <p className="text-sm text-muted-foreground">Belum ada sekolah binaan yang ditagih pada periode ini.</p>}
        </section>

        <section className="space-y-4">
          <div className="rounded-2xl border bg-card p-5 space-y-3" data-testid="mitra-payment-checkout">
            <h2 className="flex items-center gap-2 text-base font-bold">
              <QrCode className="h-4 w-4 text-[#0B3D91]" aria-hidden /> Setor kulakan ke founder
            </h2>
            <p className="text-sm text-muted-foreground">
              Kulakan periode {d.periode}: <span className="font-semibold text-foreground">{uang(g.nominal_kulakan)}</span> untuk {g.akun} akun.
              Sudah terverifikasi {uang(g.terbayar_ke_founder)}{g.menunggu_verifikasi > 0 ? `, menunggu verifikasi ${uang(g.menunggu_verifikasi)}` : ""}.
            </p>
            {f.qris_image ? (
              <div className="rounded-xl border p-3 grid place-items-center bg-muted/40" data-testid="mitra-payment-founder-qris">
                <img src={f.qris_image} alt="QRIS founder" className="max-h-56 rounded-lg" />
              </div>
            ) : (
              <div className="rounded-xl border p-4 text-center bg-muted/40" data-testid="mitra-payment-founder-qris-empty">
                <QrCode className="h-6 w-6 text-muted-foreground mx-auto mb-1" aria-hidden />
                <p className="text-xs text-muted-foreground">Founder belum memasang QRIS. Gunakan kanal berikut.</p>
              </div>
            )}
            <p className="text-sm break-words">{founderKanal || "Kanal bayar founder belum diatur — hubungi Pusat Kendali."}</p>
            <div className="space-y-3 border-t pt-3">
              <div className="space-y-2">
                <Label htmlFor="mitra-setoran-sekolah">Sekolah</Label>
                <Select value={schoolId} onValueChange={(v) => { setSchoolId(v); setJumlah(""); }}>
                  <SelectTrigger id="mitra-setoran-sekolah" data-testid="mitra-setoran-school"><SelectValue placeholder="Pilih sekolah" /></SelectTrigger>
                  <SelectContent>
                    {perSekolah.map((s) => (
                      <SelectItem key={s.school_id} value={s.school_id}>
                        {s.school_name} ({s.akun} akun · {uang(s.nominal_kulakan)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {sekolahTerpilih && (
                <div className="rounded-xl border p-3 bg-muted/30 text-sm space-y-1" data-testid="mitra-setoran-rincian">
                  <p className="font-semibold">{sekolahTerpilih.school_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {sekolahTerpilih.akun} akun · kulakan {uang(sekolahTerpilih.nominal_kulakan)} · sudah disetor {uang(sekolahTerpilih.sudah_disetor || 0)}
                  </p>
                  <p className="text-xs font-medium text-[#E11D2E]">Sisa: {uang(sisaSekolah)}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="mitra-setoran-jumlah">Jumlah setoran (rupiah)</Label>
                <Input id="mitra-setoran-jumlah" inputMode="numeric" placeholder={String(sisaSekolah || "")}
                  value={jumlah} onChange={(e) => setJumlah(e.target.value)} data-testid="mitra-setoran-amount" />
                <p className="text-xs text-muted-foreground">Contoh: {uang(sisaSekolah || g.sisa_kulakan)}.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mitra-setoran-metode">Metode</Label>
                <Select value={metode} onValueChange={setMetode}>
                  <SelectTrigger id="mitra-setoran-metode" data-testid="mitra-setoran-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="qris">QRIS</SelectItem>
                    <SelectItem value="transfer">Transfer bank</SelectItem>
                    <SelectItem value="tunai">Tunai</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mitra-setoran-catatan">Catatan (opsional)</Label>
                <Input id="mitra-setoran-catatan" placeholder="Contoh: transfer 4 Nov via BCA" value={catatan}
                  onChange={(e) => setCatatan(e.target.value)} data-testid="mitra-setoran-note" />
              </div>
              <Button onClick={setor} disabled={busy === "setor"} data-testid="mitra-setoran-submit">
                {busy === "setoran" ? <Spinner /> : <Send className="h-4 w-4 mr-1.5" />} Laporkan setoran
              </Button>
              <p className="text-xs text-muted-foreground">Laporan Anda diverifikasi founder dulu sebelum dihitung lunas.</p>
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-5 space-y-3" data-testid="mitra-payment-history">
            <h2 className="flex items-center gap-2 text-base font-bold"><Landmark className="h-4 w-4 text-[#0B3D91]" aria-hidden /> Riwayat setoran {d.periode}</h2>
            {g.setoran.length === 0 ? (
              <p className="text-sm text-muted-foreground">Belum ada setoran pada periode ini.</p>
            ) : (
              <ul className="space-y-2">
                {g.setoran.map((s) => (
                  <li key={s.id} className="rounded-xl border p-3 flex flex-wrap items-center justify-between gap-2"
                      data-testid={`mitra-payment-setoran-${s.id}`}>
                    <div>
                      <p className="font-semibold tabular-nums">{uang(s.amount_idr)}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.rincian?.school_name && <span className="font-medium text-foreground">{s.rincian.school_name} · </span>}
                        {s.metode}{s.catatan ? ` · ${s.catatan}` : ""} · {new Date(s.at).toLocaleDateString("id-ID", { day: "numeric", month: "short" })}
                      </p>
                    </div>
                    <StatusBadge map={SETTLEMENT_STATUS} value={s.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
